import { PortableTextBlock } from '@portabletext/react';
import clsx from 'clsx';
import { Link } from 'gatsby';
import { useContext } from 'react';
import { twMerge } from 'tailwind-merge';

import { Container } from 'components/GridV2';
import { ArticleCard } from 'components/rebrand/ArticleCard';
import { Button } from 'components/rebrand/Button';
import CustomPortableText from 'components/rebrand/CustomPortableText';

import { IQueryParams, ISanityBlogArticle, ISanityImage, ITrackEventProps } from 'typescriptDefs';

import { GetKOHOBtn } from './rebrand/GetKOHOBtn';
import { LocaleContext } from './rebrand/Layout';
import { H2 } from './rebrand/Typography';

const ArticlePlaceholder = () => {
  return (
    <div className="w-full">
      <div className="mb-35 h-288 w-full animate-pulse rounded-20 bg-grey-100 xs:h-400 md:mb-16 md:h-238" />
      <div className="mb-12 h-16 w-[20%] animate-pulse rounded-10 bg-grey-100 md:mb-16" />
      <div className="mb-8 h-24 w-[60%] animate-pulse rounded-10 bg-grey-100 md:mb-16 md:h-28" />
      <div className="mb-12 h-20 animate-pulse rounded-10 bg-grey-100 md:h-24" />
      <div className="mb-12 h-20 animate-pulse rounded-10 bg-grey-100 md:h-24" />
      <div className="mb-12 h-20 animate-pulse rounded-10 bg-grey-100 md:h-24" />
      <div className="h-20 w-[65%] animate-pulse rounded-10 bg-grey-100 md:h-24" />
    </div>
  );
};

type Props = {
  articles?: (Omit<ISanityBlogArticle, 'img'> & { img: { url?: string } | ISanityImage })[];
  className?: string;
  heading: string | PortableTextBlock | PortableTextBlock[];
  isLoading?: boolean;
  linkText?: string;
  linkUrl?: string;
  numArticlesToShow?: number;
  registrationTracking?: { queryParams: IQueryParams; trackEventProps: ITrackEventProps };
};

const Blogs = ({
  articles,
  className,
  heading,
  isLoading = false,
  linkText,
  linkUrl,
  numArticlesToShow = 3,
  registrationTracking,
}: Props) => {
  const locale = useContext(LocaleContext);
  const buttonText = linkText ?? { en: 'Explore our blog', fr: 'Explorez notre blog' }[locale];
  return (
    <Container className={twMerge(clsx('flex flex-col items-center px-32 xs:px-0', className))}>
      <div className="mb-32 text-center md:[&_p]:w-[70%] [&_p]:mx-auto [&_p]:text-grey-300">
        {typeof heading === 'string' ? (
          <H2 className="mb-8 text-primary-400 md:mb-20" headingStyle>
            {heading}
          </H2>
        ) : (
          <CustomPortableText
            headingStyles={{ h2: { className: 'mb-8 text-primary-400 md:mb-20', headingStyle: true } }}
            content={heading}
          />
        )}
      </div>
      {registrationTracking ? (
        <GetKOHOBtn
          className="mb-64 block"
          buttonLocation="Blog articles button"
          queryParams={registrationTracking.queryParams}
          trackEventProps={registrationTracking.trackEventProps}
        >
          {buttonText}
        </GetKOHOBtn>
      ) : (
        <Link className="mb-64" to={linkUrl ?? ''}>
          <Button>{buttonText}</Button>
        </Link>
      )}
      <div className="flex w-full flex-col gap-32 md:flex-row">
        {isLoading && (
          <>
            {[...Array(numArticlesToShow)].map((_, i) => (
              <ArticlePlaceholder key={i} />
            ))}
          </>
        )}
        {articles
          ?.slice(0, Math.min(numArticlesToShow, articles.length))
          ?.map((article) => <ArticleCard key={article.slug.current} {...article} />)}
      </div>
    </Container>
  );
};

export default Blogs;
